import {useQuery} from 'react-query'
import {useAuth} from '../app/modules/auth'
import {fetchClaims, fetchMyTurnClaims} from '../services/claimsService'

export interface ClaimsParams {
  searchType: string
  searchQuery: string | undefined
  currentPage: number
  pageSize: number
  activeTab: 'all' | 'myturn'
}

export interface ClaimsResponse {
  items: any[]
  totalPages: number
  totalRecords: number
}

export const useClaims = (params: ClaimsParams) => {
  const {currentUser} = useAuth()

  const {
    data: claimsData,
    isLoading,
    error,
    refetch,
    isFetching,
    isError,
  } = useQuery<ClaimsResponse>(
    ['claims', params, currentUser?.providerCode],
    () => {
      if (params.activeTab === 'myturn') {
        return fetchMyTurnClaims(params, currentUser)
      }
      return fetchClaims(params, currentUser)
    },
    {
      enabled: !!currentUser?.providerCode,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      retry: 1,
      onError: (error: any) => {
        console.error('Claims query error:', error)
      },
    }
  )

  return {
    data: claimsData,
    isLoading,
    error,
    refetch,
    isFetching,
    isError,
    claims: claimsData?.items || [],
    totalPages: claimsData?.totalPages || 0,
    totalRecords: claimsData?.totalRecords || 0,
  }
}

export const useMyTurnTotal = () => {
  const {currentUser} = useAuth()

  const {data: myTurnData} = useQuery<ClaimsResponse>(
    ['myTurnTotal', currentUser?.providerCode],
    () =>
      fetchMyTurnClaims(
        {
          searchType: '',
          searchQuery: '',
          currentPage: 1,
          pageSize: 1,
          activeTab: 'myturn',
        },
        currentUser
      ),
    {
      enabled: !!currentUser?.providerCode,
      staleTime: 2 * 60 * 1000, // 2 minutes
      cacheTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    }
  )

  return {
    totalRecords: myTurnData?.totalRecords || 0,
  }
}
